'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { sendGTMEvent } from '@next/third-parties/google';
import { AnimatePresence } from 'motion/react';
import { useReCaptcha } from 'next-recaptcha-v3';
import { usePathname, useRouter } from 'next/navigation';
import React, { JSX, useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useOnClickOutside } from 'usehooks-ts';
import { z } from 'zod';
import { useServerAction } from 'zsa-react';

import { Button } from '@natu/ui/Button';
import { DynamicIcon } from '@natu/ui/DynamicIcon';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Checkbox,
  Input,
  PhoneInput,
  RadioGroup,
  RadioGroupItem,
  Textarea,
} from '@natu/ui/Form';
import { Loader } from '@natu/ui/Loader';
import { Typography } from '@natu/ui/Typography';
import { debounce } from '@natu/utils/debounce';
import { PredefinedGAEvents } from '@natu/utils/events';
import { getEventPayload, type EventProps } from '@natu/utils/getEventPayload';
import { usePageContext } from '@natu/utils/PageContextProvider';
import { paths } from '@natu/utils/paths';

import { getAnEstimateFormSchema, getAnEstimateRecaptchaAction } from './getAnEstimateFormSchema';
import { sendAnEstimateOfferAction } from './sendAnEstimateOfferAction';

type CustomFormEvents = 'start' | 'abandon' | 'error' | 'success';

interface GetAnEstimateFormContentProps {
  formTitleTag?: 'h1' | 'h2' | 'h3';
  formTitle?: string;
  privacyNote: JSX.Element | null;
  marketingNote?: string;
  errorMessage?: string;
  sendEvent?: boolean;
  event_form_name?: string;
  event_internal_campaign_name?: string;
}

export const GetAnEstimateFormContent = ({
  formTitleTag,
  formTitle,
  privacyNote,
  marketingNote,
  errorMessage,
  sendEvent,
  event_form_name,
  event_internal_campaign_name,
}: GetAnEstimateFormContentProps) => {
  const ref = useRef<HTMLElement>(null);
  const [isStarted, setIsStarted] = useState(false);
  const [currentField, setCurrentField] = useState('');
  const { push } = useRouter();

  const form = useForm<z.infer<typeof getAnEstimateFormSchema>>({
    defaultValues: {
      company: '',
      email: '',
      name: '',
      phone: '',
      project: '',
      marketing: false,
    },
    resolver: zodResolver(getAnEstimateFormSchema),
  });

  const pathname = usePathname();
  const { isCampaign } = usePageContext() || {};

  const sendCustomEvent = (type: CustomFormEvents, error?: string) => {
    const customEventProps: Record<
      CustomFormEvents,
      { event: PredefinedGAEvents; eventProps: EventProps }
    > = {
      start: {
        event: 'MK_form_start',
        eventProps: { event_form_name },
      },
      abandon: {
        event: 'MK_form_abandoned',
        eventProps: { event_form_name, event_field_name: currentField },
      },
      success: {
        event: 'K_form_sent',
        eventProps: { event_form_name, event_internal_campaign_name },
      },
      error: {
        event: 'MK_form_error',
        eventProps: { event_form_name, event_error_type: error || 'not defined' },
      },
    };

    const eventPayload = getEventPayload({
      pathname,
      isCampaign,
      ...customEventProps[type],
    });

    sendGTMEvent({ event: customEventProps[type].event, ...eventPayload });
  };

  const handleClickOutside = (event: MouseEvent | TouchEvent | FocusEvent) => {
    if (!isStarted) return;

    const target = event.target as HTMLElement | null;

    if (
      target?.tagName === 'A' &&
      !target?.getAttribute('href')?.includes('tel') &&
      !target?.getAttribute('href')?.includes('mailto')
    ) {
      sendCustomEvent('abandon');
    }
  };

  useOnClickOutside(ref as React.RefObject<HTMLElement>, handleClickOutside);

  useEffect(() => {
    if (!isStarted || !sendEvent) return;

    const handleBeforeUnload = () => sendCustomEvent('abandon');

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isStarted, sendCustomEvent, sendEvent]);

  const { isPending, execute } = useServerAction(sendAnEstimateOfferAction, {
    onError: () => {
      toast(errorMessage || 'Something went wrong. Please try again later');
      if (sendEvent) sendCustomEvent('error', errorMessage);
    },
    onSuccess() {
      if (sendEvent) {
        sendCustomEvent('success');
      }
      push(paths.thankYou);
    },
  });

  const { executeRecaptcha } = useReCaptcha();

  const onSubmit = async (values: z.infer<typeof getAnEstimateFormSchema>) => {
    const gRecaptchaToken = await executeRecaptcha(getAnEstimateRecaptchaAction);
    await execute({ ...values, gRecaptchaToken });
  };

  const handleFormStart = debounce((field: string) => {
    if (!sendEvent) return;

    setCurrentField(field);

    if (!isStarted) {
      sendCustomEvent('start');
      setIsStarted(true);
    }
  }, 500);

  return (
    <div
      className="rounded-2xl bg-white px-6 py-8 shadow-2xl xl:px-10 xl:py-14"
      ref={ref as React.RefObject<HTMLDivElement>}
    >
      {formTitle && (
        <Typography
          component={formTitleTag || 'h2'}
          className="mb-6 font-semibold"
          variant={['display-sm-28', 'display-sm-28', 'display-lg-46']}
        >
          {formTitle}
        </Typography>
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mb-6 flex flex-col gap-y-4 xl:mb-8">
            <div className="flex flex-col gap-y-4 md:grid md:grid-cols-[224px_auto] md:gap-x-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700">Full name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="John Doe"
                        {...field}
                        className="shadow-xs border-gray-300"
                        onKeyDown={() => handleFormStart('name')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />{' '}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700">Business email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="you@company.com"
                        {...field}
                        className="shadow-xs border-gray-300"
                        onKeyDown={() => handleFormStart('email')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-col gap-y-4 md:grid md:grid-cols-[224px_auto] md:gap-x-4">
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700">Company name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Company"
                        {...field}
                        className="shadow-xs border-gray-300"
                        onKeyDown={() => handleFormStart('company')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />{' '}
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700">
                      Phone number <span className="text-gray-500">(optional)</span>
                    </FormLabel>
                    <FormControl>
                      <PhoneInput
                        defaultCountry="US"
                        placeholder="+1 (555) 000-0000"
                        {...field}
                        className="shadow-xs"
                        onKeyDown={() => handleFormStart('phone')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-col gap-y-4">
              <FormField
                control={form.control}
                name="project"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700">Project description</FormLabel>
                    <FormControl>
                      <Textarea
                        className="min-h-24"
                        placeholder="Leave us a message..."
                        {...field}
                        onKeyDown={() => handleFormStart('project')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="service"
                render={({ field }) => (
                  <FormItem>
                    <div className="mb-4">
                      <FormLabel className="text-gray-700">Tell us what you need:</FormLabel>
                    </div>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className="flex flex-col space-y-1 sm:grid sm:grid-cols-4 sm:space-y-0"
                        onClick={() => handleFormStart('service')}
                      >
                        <FormItem className="flex items-center space-x-3 space-y-0 sm:justify-start">
                          <FormControl>
                            <RadioGroupItem className="border-gray-300" value="e-commerce" />
                          </FormControl>
                          <FormLabel className="font-normal">E-commerce</FormLabel>
                        </FormItem>
                        <FormItem className="flex items-center space-x-3 space-y-0 sm:justify-center">
                          <FormControl>
                            <RadioGroupItem className="border-gray-300" value="app" />
                          </FormControl>
                          <FormLabel className="font-normal">App</FormLabel>
                        </FormItem>
                        <FormItem className="flex items-center space-x-3 space-y-0 sm:justify-center">
                          <FormControl>
                            <RadioGroupItem className="border-gray-300" value="website" />
                          </FormControl>
                          <FormLabel className="font-normal">Website</FormLabel>
                        </FormItem>
                        <FormItem className="flex items-center space-x-3 space-y-0 sm:justify-center">
                          <FormControl>
                            <RadioGroupItem className="border-gray-300" value="other" />
                          </FormControl>
                          <FormLabel className="font-normal">Other</FormLabel>
                        </FormItem>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          {privacyNote}
          <FormField
            control={form.control}
            name="marketing"
            render={({ field }) => (
              <FormItem className="mb-8 mt-2 flex flex-row items-center space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onClick={() => handleFormStart('privacy')}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel>
                  <p className="cursor-pointer text-xs font-thin leading-[1.3] text-[#4B5565] opacity-80">
                    {marketingNote}
                  </p>
                </FormLabel>
              </FormItem>
            )}
          />
          <Button
            disabled={form.formState.isSubmitting}
            variant="primary"
            type="submit"
            className="flex w-48 items-center gap-x-2 text-[0.813rem] font-semibold uppercase leading-[1.5rem]"
          >
            Send
            <DynamicIcon icon="arrowRight" />
            <AnimatePresence mode="wait">{isPending && <Loader />}</AnimatePresence>
          </Button>
        </form>
      </Form>
    </div>
  );
};
